import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

/** One lag in the forvalter's portfolio. */
export interface PortfolioLag {
  tenantId: string;
  name: string;
  type: string;
  status: string;
  unitCount: number;
}

/** The forvalter's own view: org identity + branding + portfolio. */
export interface ForvalterPortfolio {
  orgId: string;
  orgName: string;
  accentColorToken: string | null;
  lag: PortfolioLag[];
}

/** Outcome of switching into a lag — a board-scoped (Forvalter-role) token. */
export interface SwitchResult {
  tenantId: string;
  accessToken: string;
  expiresAtUtc: string;
}

/** A reusable cross-lag message template. */
export interface MessageTemplateView {
  id: string;
  name: string;
  title: string;
  body: string;
  updatedAtUtc: string;
}

/** Per-lag outcome of a mass-send. */
export interface MassSendOutcome {
  tenantId: string;
  lagName: string;
  published: boolean;
  postId: string | null;
  error: string | null;
}

/**
 * Typed client for `/api/forvalter` (V2.5 slice A) — the forvalter CUSTOMER portal.
 * The server derives the portfolio from the caller's forvalter-org membership;
 * requests never carry scope the caller chooses.
 */
@Injectable({ providedIn: 'root' })
export class ForvalterService {
  private readonly http = inject(HttpClient);
  private static readonly Base = '/api/forvalter';

  /** Switch into an own lag → a board-scoped token (adopt via AuthService.adoptSession). */
  switch(tenantId: string): Observable<SwitchResult> {
    return this.http.post<SwitchResult>(`${ForvalterService.Base}/switch/${encodeURIComponent(tenantId)}`, {});
  }

  massSend(title: string, body: string, importance: string, tenantIds: string[]): Observable<MassSendOutcome[]> {
    return this.http.post<MassSendOutcome[]>(`${ForvalterService.Base}/mass-send`, { title, body, importance, tenantIds });
  }

  saveTemplate(name: string, title: string, body: string): Observable<MessageTemplateView> {
    return this.http.post<MessageTemplateView>(`${ForvalterService.Base}/templates`, { name, title, body });
  }

  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${ForvalterService.Base}/templates/${encodeURIComponent(id)}`);
  }
}
